/**
 * v4 Ask — refined chat. AI tries first; user can escalate to accountant
 * on any reply. Suggested-question chips for low-tech users; full keyboard
 * input for everyone.
 */

import { useState } from 'react';
import { Send, User, Sparkles, ArrowUpRight, History, X, Plus, MessageCircle } from 'lucide-react';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';

interface Msg {
  id: number;
  from: 'user' | 'ai';
  text: string;
  forwardable?: boolean;
  forwardedToHuman?: boolean;
}

const SUGGESTED_QS = [
  'How much profit did I make this month?',
  'When is my next BAS due?',
  'What did I spend the most on?',
  'How much do my customers owe me?',
];

interface Conversation {
  id: string;
  title: string;
  lastSnippet: string;
  when: string;
  messages: Msg[];
}

const HISTORY: Conversation[] = [
  { id: 'c1', title: 'Q3 BAS preparation',          when: '2 days ago',  lastSnippet: 'Draft total is $7,820 — Marcus will lodge once you approve.',
    messages: [
      { id: 1, from: 'user', text: 'When is my next BAS due?' },
      { id: 2, from: 'ai',   text: 'Your next BAS is due in 12 days. The draft total is $7,820. Marcus has prepared it — you will get a notification once it is ready to lodge.', forwardable: true },
    ] },
  { id: 'c2', title: 'Biggest expense this month',  when: '5 days ago',  lastSnippet: 'Materials $8,920 (56%) was your biggest expense.',
    messages: [
      { id: 1, from: 'user', text: 'What did I spend the most on?' },
      { id: 2, from: 'ai',   text: 'This month your largest expense was Materials ($8,920 — 56% of total). Then Rent ($2,800), then Wages ($2,100).', forwardable: true },
    ] },
  { id: 'c3', title: 'Unpaid invoices',              when: '1 week ago',  lastSnippet: 'Three customers owe you $12,480. Oldest is 28 days.',
    messages: [
      { id: 1, from: 'user', text: 'How much do my customers owe me?' },
      { id: 2, from: 'ai',   text: 'You have $12,480 in unpaid invoices — three customers. The oldest is 28 days. Marcus can chase them if you would like.', forwardable: true },
    ] },
  { id: 'c4', title: 'EOFY checklist',               when: '2 weeks ago', lastSnippet: 'Five things to sort before 30 June…',
    messages: [
      { id: 1, from: 'user', text: 'What do I need to do before end of financial year?' },
      { id: 2, from: 'ai',   text: 'Five things before 30 June: review vehicle logbook, gather asset write-off receipts (under $20k), make any super top-ups, prepay deductible expenses, write off bad debts.', forwardable: true },
    ] },
];

export default function AskPageV4() {
  const { t, dir } = useV4();
  const [messages, setMessages] = useState<Msg[]>([
    { id: 1, from: 'ai', text: t('ask_hint') },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadConversation = (c: Conversation) => {
    setMessages(c.messages);
    setShowHistory(false);
  };

  const newChat = () => {
    setMessages([{ id: 1, from: 'ai', text: t('ask_hint') }]);
    setShowHistory(false);
  };

  const respond = (userText: string) => {
    setThinking(true);
    setTimeout(() => {
      const reply: Msg = {
        id: Date.now(),
        from: 'ai',
        text: canned(userText),
        forwardable: true,
      };
      setMessages(m => [...m, reply]);
      setThinking(false);
    }, 900);
  };

  const send = (txt: string) => {
    const text = txt.trim();
    if (!text) return;
    setMessages(m => [...m, { id: Date.now(), from: 'user', text }]);
    setInput('');
    respond(text);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const forward = (id: number) => {
    setMessages(m => m.map(msg => msg.id === id ? { ...msg, forwardedToHuman: true, forwardable: false } : msg));
    setMessages(m => [...m, {
      id: Date.now(),
      from: 'ai',
      text: t('forwarded_to_accountant'),
    }]);
  };

  return (
    <div className="flex h-[calc(100vh-160px)] flex-col">
      {/* Header */}
      <div className="pb-3 pt-1 flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('ask_title')}</p>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t('ask_hint')}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={newChat}
            title="New chat"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            onClick={() => setShowHistory(true)}
            title="Chat history"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors"
          >
            <History className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-32">
        {messages.map(msg => (
          <div key={msg.id} className={cn('flex gap-2', msg.from === 'user' ? 'justify-end' : 'justify-start')}>
            {msg.from === 'ai' && (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
            )}
            <div className={cn('flex flex-col gap-1.5', msg.from === 'user' ? 'items-end' : 'items-start')}>
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  msg.from === 'user'
                    ? 'bg-[var(--v4-accent)] text-white rounded-br-md'
                    : 'bg-card border border-border text-foreground rounded-bl-md',
                  dir === 'rtl' && msg.from === 'user' && 'rounded-bl-md rounded-br-2xl',
                  dir === 'rtl' && msg.from === 'ai' && 'rounded-br-md rounded-bl-2xl'
                )}
              >
                {msg.text}
              </div>
              {msg.forwardable && !msg.forwardedToHuman && (
                <button
                  onClick={() => forward(msg.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/80 transition-colors hover:border-muted-foreground/60 hover:text-foreground"
                >
                  <User className="h-3 w-3" />
                  {t('ask_marcus_instead')}
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </div>
            {msg.from === 'user' && (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-foreground/80">
                <User className="h-3.5 w-3.5" />
              </div>
            )}
          </div>
        ))}

        {thinking && (
          <div className="flex gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Suggested questions chips — only on first turn */}
        {messages.length === 1 && (
          <div className="pt-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60 mb-2">Try asking</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_QS.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-[12px] text-foreground/80 hover:border-muted-foreground/40 hover:text-foreground transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={onSubmit} className="fixed bottom-20 left-0 right-0 z-20 mx-auto max-w-[720px] px-4">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-1 shadow-md">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('type_question')}
            className="flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            aria-label={t('send')}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--v4-accent)] text-white shadow-sm transition-all hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send className={cn('h-4 w-4', dir === 'rtl' && '-scale-x-100')} />
          </button>
        </div>
      </form>

      {/* Chat history sheet */}
      {showHistory && (
        <>
          <div onClick={() => setShowHistory(false)} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <div className="fixed inset-x-0 bottom-0 top-12 z-50 mx-auto flex max-w-[720px] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-background shadow-2xl animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-[var(--v4-accent-text)]" strokeWidth={1.75} />
                <h2 className="text-sm font-bold">Chat history</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={newChat}
                  className="inline-flex items-center gap-1 rounded-md bg-[var(--v4-accent)] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[var(--v4-accent-hover)]"
                >
                  <Plus className="h-3 w-3" /> New chat
                </button>
                <button onClick={() => setShowHistory(false)} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ul className="divide-y divide-border/60">
                {HISTORY.map(c => (
                  <li key={c.id}>
                    <button
                      onClick={() => loadConversation(c)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/40"
                    >
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-[var(--v4-accent-tint)] text-[var(--v4-accent-text)]">
                        <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground truncate">{c.title}</p>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">{c.when}</span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 leading-snug">{c.lastSnippet}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function canned(q: string): string {
  const lower = q.toLowerCase();
  if (lower.includes('tax') || lower.includes('bas') || lower.includes('مالی')) {
    return 'Your next BAS is due in 12 days (Q3 2026). The draft total is $7,820. Marcus has prepared it for you — you will receive a notification once it is ready to lodge.';
  }
  if (lower.includes('profit') || lower.includes('made') || lower.includes('پول')) {
    return 'This month you made $21,240 in revenue. After $15,870 of expenses, your profit is $5,370 — a 25.3% margin. That is +8% compared to last month.';
  }
  if (lower.includes('expense') || lower.includes('spent')) {
    return 'This month your largest expense was Materials ($8,920 — 56% of total). Then Rent ($2,800), then Wages ($2,100).';
  }
  if (lower.includes('owe') || lower.includes('customer') || lower.includes('invoice')) {
    return 'You have $12,480 in unpaid invoices — three customers. The oldest is 28 days. Marcus can chase them if you would like.';
  }
  return 'I am not sure about that one. Tap "Ask Marcus instead" and your accountant will help.';
}
